import { AssetMap } from './asset-map.js'
import { CanvasRepository } from './canvas-repository.js'

function decodeBase64Png(value) {
  if (!value) return null
  const binary = atob(String(value).replace(/^data:image\/png;base64,/, ''))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return new Blob([bytes], { type: 'image/png' })
}

function writeRichWithCopyEvent(text, html) {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false
  let handled = false
  const onCopy = event => {
    if (!event.clipboardData) return
    event.clipboardData.setData('text/plain', text)
    event.clipboardData.setData('text/html', html)
    event.preventDefault()
    handled = true
  }
  document.addEventListener('copy', onCopy, true)
  try {
    return document.execCommand('copy') && handled
  } catch {
    return false
  } finally {
    document.removeEventListener('copy', onCopy, true)
  }
}

async function writeRichClipboard(text, html, pngBase64) {
  const plain = String(text ?? '')
  const rich = String(html ?? '')
  const clipboard = navigator.clipboard
  let richWriteError = null

  if (typeof clipboard?.write === 'function' && typeof ClipboardItem === 'function' && rich) {
    const parts = {
      'text/plain': new Blob([plain], { type: 'text/plain' }),
      'text/html': new Blob([rich], { type: 'text/html' }),
    }
    const png = decodeBase64Png(pngBase64)
    if (png) parts['image/png'] = png
    try {
      await clipboard.write([new ClipboardItem(parts)])
      return true
    } catch (error) {
      // Some Safari versions expose ClipboardItem but reject a multi-type item
      // containing PNG. Retry the Canvas marker without the external-app image
      // before falling back to plain text.
      if (png) {
        try {
          delete parts['image/png']
          await clipboard.write([new ClipboardItem(parts)])
          return true
        } catch {}
      }
      richWriteError = error
    }
  }

  // Safari/WebKit builds without ClipboardItem can still write multiple MIME
  // types synchronously while handling the user's copy shortcut.
  if (rich && writeRichWithCopyEvent(plain, rich)) return true
  if (typeof clipboard?.writeText !== 'function') throw richWriteError || new Error('clipboard_unavailable')
  await clipboard.writeText(plain)
  return false
}

export function createWebCanvasHost() {
  const assets = new AssetMap()
  const repository = new CanvasRepository(assets)
  return Object.freeze({
    platform: 'web',
    capabilities: Object.freeze({
      upload: true, download: true, video: true, model3d: true, mask: false,
      generation: false, agent: false, builtinActions: false, workflow: false,
      localVoice: false, localFiles: false, desktopIntegrations: false,
    }),
    canvases: {
      list: () => repository.list(), load: (id, viewportSize) => repository.load(id, viewportSize),
      stageLocal: (id, document, version, viewportSize) => repository.stageLocal(id, document, version, viewportSize),
      save: (id, document, version, viewportSize) => repository.save(id, document, version, viewportSize),
      flushView: id => repository.flushView(id),
      create: name => repository.create(name), rename: (id, name) => repository.rename(id, name),
      duplicate: (id, source) => repository.duplicate(id, source), delete: id => repository.delete(id),
      discardLocal: id => repository.discardLocal(id),
      subscribe: (id, listener) => repository.subscribe(id, listener),
    },
    assets: {
      prime: document => assets.prime(document), resolve: uri => assets.resolve(uri),
      poster: uri => assets.poster(uri), resolvePoster: uri => assets.resolvePoster(uri),
      importFile: file => assets.importFile(file), retry: uri => assets.retry(uri), cancel: uri => assets.cancel(uri),
      download: (uri, filename) => assets.download(uri, filename), releaseAll: () => assets.releaseAll(),
    },
    clipboard: {
      writeText: text => navigator.clipboard.writeText(String(text)),
      writeRich: (text, html, pngBase64) => writeRichClipboard(text, html, pngBase64),
    },
    shell: {
      back: () => { location.href = '/canvases' },
      account: () => { location.href = '/canvases?account=1' },
    },
    _repository: repository,
    _assetMap: assets,
  })
}
